export function buildAssistantSettingsSummary(snapshot) {
    const assistant = snapshot?.assistant || {};
    const flowContext = snapshot?.flowContext || {};
    const generationIr = snapshot?.generationIr || {};
    const steps = Array.isArray(generationIr.steps) ? generationIr.steps.length : 0;
    const skillCount = Array.isArray(snapshot?.project?.skillIds) ? snapshot.project.skillIds.length : 0;
    const profileLabel = assistant.plcProfileName || assistant.dialectLabel || assistant.dialect || "Generic IEC ST";
    return `${profileLabel} / FlowContext v${flowContext.contractVersion} / IR v${generationIr.contractVersion} / ${steps} steps / Skill: ${skillCount}件`;
}

export function setQuestionDraftInput(chat = {}, value = "", notify = () => {}) {
    chat.questionDraftInput = String(value || "");
    if (chat.questionCardError) chat.questionCardError = "";
    notify();
    return chat.questionDraftInput;
}

export function toggleTaskHistoryPanel({
    chat = {},
    renderChatUi = () => {},
    loadHistory = () => Promise.resolve(),
    onHistoryLoadError = () => {}
} = {}) {
    const nextOpen = !chat.taskHistoryOpen;
    chat.taskHistoryOpen = nextOpen;
    renderChatUi();
    if (nextOpen && chat.historyItems.length === 0 && !chat.historyLoading) {
        Promise.resolve(loadHistory()).catch((error) => {
            onHistoryLoadError(error);
        });
    }
    return nextOpen;
}

export function refreshTaskHistoryPanel(loadHistory = () => Promise.resolve(), onHistoryLoadError = () => {}) {
    return Promise.resolve(loadHistory()).catch((error) => {
        onHistoryLoadError(error);
    });
}

export function requestThreadTitleFromAi({
    threadId = "",
    message = "",
    chat = {},
    createAssistantTitle = null,
    renderChatUi = () => {},
    onError = () => {}
} = {}) {
    if (typeof createAssistantTitle !== "function") return Promise.resolve(false);
    return Promise.resolve(createAssistantTitle({ message }))
        .then((result) => {
            const thread = chat.threads?.[threadId];
            if (!thread || chat.activeThreadId !== threadId) return false;
            if (!result?.title) return false;
            thread.label = result.title;
            renderChatUi();
            return true;
        })
        .catch((error) => {
            onError(error);
            return false;
        });
}

export async function submitPlanAnswerFromInput({
    text = "",
    chat = {},
    question = null,
    answerPlanQuestion = async () => {}
} = {}) {
    if (!question?.id) return false;
    chat.questionDraftInput = text;
    await answerPlanQuestion(question.id, text, text);
    return true;
}

export async function submitChatComposer({
    text = "",
    chat = {},
    question = null,
    agentSession = null,
    ensureActiveChatThread = () => null,
    buildFallbackThreadTitle = () => "新規チャット",
    renderChatUi = () => {},
    requestThreadTitleFromAi = () => {},
    submitPlanAnswerFromInput = async () => false,
    runChatAction = async () => {},
    setChatStatus = () => {}
} = {}) {
    if (chat.busy) return false;
    const message = String(text || "").trim();
    if (!message) {
        setChatStatus(question?.kind === "text" ? "回答を入力してください" : "追加指示を入力してください");
        return false;
    }
    const thread = ensureActiveChatThread();
    const shouldCreateTitle = thread?.messages?.length === 0 && (!thread.label || thread.label === "新規チャット");
    if (shouldCreateTitle) {
        thread.label = buildFallbackThreadTitle(message);
        renderChatUi();
        requestThreadTitleFromAi(thread.id, message);
    }
    if (agentSession?.active && ["waiting_answer", "asking"].includes(agentSession.status)) {
        chat.busy = true;
        renderChatUi();
        try {
            return await submitPlanAnswerFromInput(message);
        } finally {
            chat.busy = false;
            renderChatUi();
        }
    }
    await runChatAction(chat.mode || "st", message);
    return true;
}

export function presentCurrentPlanQuestion({
    session = null,
    question = null,
    resetQuestionCardState = () => {},
    buildQuestionUi = null,
    buildPlanReasonText = () => "",
    setAgentStatus = () => {},
    appendChatMessage = () => {},
    createQuestionCardPayload = () => null,
    setChatStatus = () => {},
    touchAgentSession = () => {}
} = {}) {
    if (!session?.active || !question) return false;
    resetQuestionCardState();
    const questionUi = typeof buildQuestionUi === "function"
        ? buildQuestionUi(session, question)
        : {
            agentStatus: "asking",
            chatStatus: question.kind === "text" ? "Waiting for input" : "Waiting for selection",
            message: `Question ${session.currentQuestionIndex + 1}/${session.questions.length}\n${buildPlanReasonText(question)}\n\nQuestion: ${question.text}`,
            meta: "agent-question",
            actions: []
        };
    setAgentStatus(questionUi.agentStatus || "asking");
    appendChatMessage("assistant", questionUi.message, questionUi.meta || "agent-question", {
        actions: questionUi.actions || [],
        question: createQuestionCardPayload(question, questionUi)
    });
    setChatStatus(questionUi.chatStatus || (question.kind === "text" ? "Waiting for input" : "Waiting for selection"));
    touchAgentSession(session);
    return true;
}

export async function answerPlanQuestion({
    questionId = "",
    rawValue = "",
    answerLabel = "",
    chat = {},
    session = null,
    question = null,
    hasApiGenerationClient = false,
    renderChatUi = () => {},
    setChatStatus = () => {},
    applyAnswerToSession = null,
    applyPlanAnswerToDraft = () => {},
    syncAgentJobState = () => {},
    buildAnswerUi = null,
    setAgentStatus = () => {},
    appendChatMessage = () => {},
    pushPlanActivity = () => {},
    pushAgentMemory = () => {},
    buildAgentRevisionInstruction = () => "",
    refreshAgentDraftFromSources = async () => null,
    touchAgentSession = () => {},
    getLatestQuestion = () => null,
    runAgentLoopRuntime = () => {},
    ensureAgentSessionPolling = () => {}
} = {}) {
    if (!session?.active || !question || question.id !== questionId) return false;
    let normalized = rawValue;
    chat.questionCardError = "";
    try {
        if (typeof question.normalize === "function") {
            normalized = question.normalize(rawValue);
        }
    } catch (error) {
        chat.questionCardError = error.message || "Could not understand that answer.";
        renderChatUi();
        setChatStatus("Waiting for answer");
        return false;
    }
    chat.questionDraftInput = "";

    if (typeof applyAnswerToSession === "function") {
        applyAnswerToSession(session, questionId, normalized);
    } else {
        session.currentStepIndex = 4;
        session.status = "revising";
        session.stateAuthority = "client";
        session.answers[questionId] = normalized;
        applyPlanAnswerToDraft(session.draft, questionId, normalized);
        session.revisionCount += 1;
        session.currentQuestionIndex = 0;
    }
    syncAgentJobState(session);
    const resolvedAnswerLabel = answerLabel || String(normalized);
    const answerUi = typeof buildAnswerUi === "function"
        ? buildAnswerUi(questionId, normalized, resolvedAnswerLabel, hasApiGenerationClient)
        : {
            agentStatus: "revising",
            answerText: resolvedAnswerLabel,
            activity: {
                message: `Action: Applied answer for ${questionId}: ${String(normalized)}`,
                meta: "agent-action"
            },
            confirmedFact: `${questionId}: ${String(normalized)}`,
            refreshConfig: {
                chatStatus: hasApiGenerationClient ? "Updating plan with AI..." : "Updating plan locally...",
                remoteSuccessMessage: "Action: Loaded revised draft from the remote agent session.",
                aiSuccessMessage: "Action: Applied revised AI draft.",
                localFallbackMessage: "Observation: AI revise unavailable, keeping the local draft.",
                quotaFallbackMessage: "Observation: AI quota reached, keeping the local draft.",
                errorMessage: "Observation: Failed to revise with AI, keeping the local draft."
            }
        };
    setAgentStatus(answerUi.agentStatus || "revising");
    appendChatMessage("user", answerUi.answerText, "agent-answer");
    pushPlanActivity(answerUi.activity?.message || `Action: Applied answer for ${questionId}: ${String(normalized)}`, answerUi.activity?.meta || "agent-action");
    if (answerUi.confirmedFact) pushAgentMemory("confirmedFacts", answerUi.confirmedFact);
    const revisionRefreshConfig = answerUi.refreshConfig || {};
    setChatStatus(revisionRefreshConfig.chatStatus || (hasApiGenerationClient ? "Updating plan with AI..." : "Updating plan locally..."));
    const revisedInstruction = buildAgentRevisionInstruction(session);
    const refreshResult = await refreshAgentDraftFromSources(session, revisedInstruction, {
        stage: "revise",
        ...revisionRefreshConfig
    });

    touchAgentSession(session);
    if (refreshResult?.source === "conflict") {
        const latestQuestion = getLatestQuestion();
        const promptLine = latestQuestion?.text
            ? `Latest question: ${latestQuestion.text}`
            : "Please review the latest server-side plan state before answering again.";
        appendChatMessage("assistant", `The server session changed while I was revising the plan, so I reloaded the latest session.\n${promptLine}`, "agent-observation");
        setChatStatus(latestQuestion ? "Latest session restored" : "Session restored from server");
    }
    runAgentLoopRuntime(session, { emitOverview: true });
    ensureAgentSessionPolling(session);
    return true;
}
